import React, { useContext, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { I18nContext } from '../context/translations/TranslationsContext.context';
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle
} from '../components/ui/alert-dialog';
import { AccesoGuardado, borrarAcceso, guardarAcceso } from './accesoGuardado';
import { tomarAccesoTrasLogin } from './accesoTrasLogin';
import { TipoDeBiometria, tipoDeBiometria } from './biometria';
import {
	activarBiometria,
	biometriaActivada,
	esDelTitular,
	olvidarBiometria,
	quiereActivarAlEntrar,
	rechazarBiometria,
	recordarTitular
} from './preferenciaBiometria';
import { esApp } from './plataforma';
import { conTipo, textosParaEntrar } from './textosBiometria';

interface Oferta {
	tipo: TipoDeBiometria;
	acceso: AccesoGuardado;
}

/**
 * «¿Entrar con Face ID la próxima vez?», justo después de entrar con NIF y contraseña.
 *
 * Si acepta, el NIF y la contraseña se guardan en la caja fuerte del móvil atados a su cara o
 * huella (`accesoGuardado.ts`); al abrir la app, el login los recupera y entra solo. Es opcional,
 * como pide Apple, pero se ofrece en CADA login con contraseña mientras no lo tenga guardado
 * (decisión de la war room del 19-oct: quien decía «Ahora no» una vez no volvía a verla nunca).
 * Si lo pidió en Configuración, se activa directamente al entrar, sin volver a preguntar.
 *
 * Lo guardado es de quien lo activó (su NIF queda como titular): si otra persona entra con su
 * contraseña en el mismo móvil, se borra y se le ofrece a ella.
 *
 * El login deja el acceso en `accesoTrasLogin` y navega; aquí se recoge en el cambio de pantalla.
 * Así no hay que tocar el formulario más que para dejarlo. En la web no hace nada.
 */
const OfertaBiometria: React.FC = () => {
	const { translate } = useContext(I18nContext).state;
	const traducir = useRef(translate);
	traducir.current = translate;
	const { pathname } = useLocation();
	const [oferta, setOferta] = useState<Oferta | null>(null);

	const activar = async (acceso: AccesoGuardado): Promise<boolean> => {
		const guardado = await guardarAcceso(acceso, textosParaEntrar(traducir.current));
		if (guardado) {
			activarBiometria();
			recordarTitular(acceso.nif);
		}

		return guardado;
	};

	const ofrecer = async (acceso: AccesoGuardado) => {
		// Otra persona ha entrado con su contraseña en este móvil: lo guardado no es suyo y
		// «Entrar con Face ID» la llevaría a la cuenta del titular. Se borra ANTES de ofrecérselo,
		// para no borrar después lo que ella guarde. Sin titular conocido (activado en la versión
		// 9), tampoco se sabe de quién es: se borra igual.
		if (biometriaActivada() && !esDelTitular(acceso.nif)) {
			await borrarAcceso();
			olvidarBiometria();
		}

		if (biometriaActivada()) {
			return;
		}
		const pedidoEnConfiguracion = quiereActivarAlEntrar();

		const tipo = await tipoDeBiometria();
		if (!tipo) {
			return;
		}
		if (pedidoEnConfiguracion) {
			// Si cancela, sigue pendiente: se volverá a intentar en el próximo login.
			void activar(acceso);
			return;
		}
		setOferta({ tipo, acceso });
	};

	useEffect(() => {
		if (!esApp()) {
			return;
		}

		// Se toma siempre: la contraseña no se queda esperando en memoria.
		const acceso = tomarAccesoTrasLogin();
		if (acceso) {
			void ofrecer(acceso);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- se mira en cada cambio de pantalla
	}, [pathname]);

	const decidir = async (quiere: boolean) => {
		const acceso = (oferta as Oferta).acceso;
		setOferta(null);

		if (!quiere || !(await activar(acceso))) {
			rechazarBiometria();
		}
	};

	if (!oferta) {
		return null;
	}

	return (
		<AlertDialog open>
			{/* El botón atrás de Android cierra los diálogos con «Escape» (`botonAtras.ts`): vale «Ahora no». */}
			<AlertDialogContent onEscapeKeyDown={() => void decidir(false)}>
				<AlertDialogHeader>
					<AlertDialogTitle>
						{conTipo(translate, 'movil.biometria.oferta.titulo', oferta.tipo)}
					</AlertDialogTitle>
					<AlertDialogDescription>
						{conTipo(translate, 'movil.biometria.oferta.texto', oferta.tipo)}
					</AlertDialogDescription>
				</AlertDialogHeader>
				<AlertDialogFooter>
					<AlertDialogCancel onClick={() => void decidir(false)}>
						{translate('movil.biometria.oferta.ahoraNo')}
					</AlertDialogCancel>
					<AlertDialogAction onClick={() => void decidir(true)}>
						{translate('movil.biometria.oferta.activar')}
					</AlertDialogAction>
				</AlertDialogFooter>
			</AlertDialogContent>
		</AlertDialog>
	);
};

export default OfertaBiometria;
